import { ReactNode } from 'react';
import { BaseEditor, Descendant } from 'slate';
import { ReactEditor } from 'slate-react';

export enum MarkFormat {
	BOLD = 'bold',
	ITALIC = 'italic',
	UNDERLINE = 'underline',
	STRIKETHROUGH = 'strikethrough',
	FONT_SIZE = 'fontSize',
	COLOR = 'color',
	BACKGROUND_COLOR = 'backgroundColor',
}

export enum BaseBlockFormat {
	PARAGRAPH = 'paragraph',
	HEADING_ONE = 'headingOne',
	HEADING_TWO = 'headingTwo',
	HEADING_THREE = 'headingThree',
	BLOCKQUOTE = 'blockquote',
	LINK = 'link',
	TWO_COLUMNS = 'twoColumns',
}

export enum AligmentFormat {
	LEFT = 'alignLeft',
	CENTER = 'alignCenter',
	RIGHT = 'alignRight',
}

export enum ListFormat {
	ORDERED = 'orderedList',
	UNORDERED = 'unorderedList',
	ITEM = 'listItem',
}

export enum EmbedBlockFormat {
	IMAGE = 'image',
	VIDEO = 'video',
}

export enum FloatFormat {
	FLOAT_LEFT = 'floatLeft',
	FLOAT_RIGHT = 'floatRight',
	CLEAR_BOTH = 'clearBoth',
}

export enum TableFormat {
	TABLE = 'table',
	TABLE_ROW = 'tableRow',
	TABLE_CELL = 'tableCell',
}

export type BlockFormat = BaseBlockFormat | AligmentFormat | ListFormat | EmbedBlockFormat | TableFormat | FloatFormat;

export type Format = MarkFormat & BlockFormat;

export const SIZE_MAP = {
	small: '0.75em',
	normal: '1em',
	medium: '1.75em',
	large: '2.5em',
} as const;

export const FONT_FAMILY_MAP = {
	sans: 'Helvetica,Arial, sans serif',
	serif: 'Georgia, Times New Roaman,serif',
	monospace: 'Monaco, Courier New,monospace',
} as const;

export type CustomText = {
	text: string;
	bold?: boolean;
	italic?: boolean;
	underline?: boolean;
	strikethrough?: boolean;
	subscript?: boolean;
	superscript?: boolean;
	color?: string;
	backgroundColor?: string;
	fontSize?: keyof typeof SIZE_MAP;
	fontFamily?: keyof typeof FONT_FAMILY_MAP;
};

// Specific element types
export type ParagraphElement = {
	type: BaseBlockFormat.PARAGRAPH;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type HeadingOneElement = {
	type: BaseBlockFormat.HEADING_ONE;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type HeadingTwoElement = {
	type: BaseBlockFormat.HEADING_TWO;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type HeadingThreeElement = {
	type: BaseBlockFormat.HEADING_THREE;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type BlockquoteElement = {
	type: BaseBlockFormat.BLOCKQUOTE;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type AlignLeftElement = {
	type: AligmentFormat.LEFT;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type AlignCenterElement = {
	type: AligmentFormat.CENTER;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type AlignRightElement = {
	type: AligmentFormat.RIGHT;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type ListElement = {
	type: ListFormat;
	children: ListItemElement[];
	attr?: Record<string, any>;
};

export type ListItemElement = {
	type: ListFormat.ITEM;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type TableElement = {
	type: TableFormat.TABLE;
	children: TableRowElement[];
	rows?: number;
	columns?: number;
	attr?: Record<string, any>;
};

export type TableRowElement = {
	type: TableFormat.TABLE_ROW;
	children: TableCellElement[];
	attr?: Record<string, any>;
};

export type TableCellElement = {
	type: TableFormat.TABLE_CELL;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type LinkElement = {
	type: BaseBlockFormat.LINK;
	url: string;
	openInNewTab?: boolean;
	attr?: Record<string, any>;
	children: CustomText[];
};

export type EmbedSpecialAttributes = {
	url: string;
	alt?: string;
	widthUnit: 'px' | '%';
	width?: string | number;
	float?: 'left' | 'right';
};

export type ImageElement = {
	type: EmbedBlockFormat.IMAGE;
	children: CustomText[];
} & EmbedSpecialAttributes;

export type VideoElement = {
	type: EmbedBlockFormat.VIDEO;
	children: CustomText[];
} & EmbedSpecialAttributes;

export type TwoColumnsElement = {
	type: BaseBlockFormat.TWO_COLUMNS;
	children: ColumnElement[];
	ratio?: string;
	attr?: Record<string, any>;
};

export type ColumnElement = {
	type: 'column';
	children: CustomText[];
};

export type FloatLeftElement = {
	type: FloatFormat.FLOAT_LEFT;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type FloatRightElement = {
	type: FloatFormat.FLOAT_RIGHT;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type ClearBothElement = {
	type: FloatFormat.CLEAR_BOTH;
	children: CustomText[];
	attr?: Record<string, any>;
};

export type CustomElement =
	| ParagraphElement
	| HeadingOneElement
	| HeadingTwoElement
	| HeadingThreeElement
	| BlockquoteElement
	| AlignLeftElement
	| AlignCenterElement
	| AlignRightElement
	| ListElement
	| ListItemElement
	| TableElement
	| TableRowElement
	| TableCellElement
	| LinkElement
	| ImageElement
	| VideoElement
	| TwoColumnsElement
	| ColumnElement
	| FloatLeftElement
	| FloatRightElement
	| ClearBothElement;

export type CustomEditor = BaseEditor &
	ReactEditor & {
		container: HTMLElement;
	};

export type EditorValue = Descendant[];

export interface RenderElementProps {
	element: CustomElement;
	children: ReactNode;
	attributes: Record<string, any>;
}

export interface RenderLeafProps {
	leaf: CustomText;
	children: ReactNode;
	attributes: Record<string, any>;
}

export type {
	RenderElementProps as SlateRenderElementProps,
	RenderLeafProps as SlateRenderLeafProps,
} from 'slate-react';

type CustomDescendant = CustomElement;
declare module 'slate' {
	interface CustomTypes {
		Editor: CustomEditor;
		Element: CustomElement;
		Text: CustomText;
		Descendant: CustomDescendant;
	}
}
